"use client";

import { c as _c } from "react/compiler-runtime";
import type { OnDragEndResponder } from "@hello-pangea/dnd";
import type { StackProps } from "@mui/material";
import React, { useState } from "react";
import { SolutionModal } from "#/features/codeRunner/ui/SolutionModal";
import { selectIsEditable } from "#/features/project/model/projectSlice";
import { DraggableSelectBarList } from "#/features/selectBar/ui/DraggableSelectBarList";
import { DraggableSelectBarChip } from "#/features/selectBar/ui/SelectBarChip";
import type { UseTRPCQueryResult } from "#/server/api/trpc";
import type { PlaygroundSolution } from "#/server/db/generated/browser";
import { api } from "#/shared/api";
import type { RouterOutputs } from "#/shared/api";
import { usePlaygroundSlugs } from "#/shared/hooks";
import { useI18nContext } from "#/shared/hooks";
import { useAppSelector } from "#/store/hooks";
type SolutionBrief = Pick<PlaygroundSolution, "id" | "slug" | "title" | "order">;
type SolutionSelectBarProps = StackProps & {
  selectedProject: UseTRPCQueryResult<RouterOutputs["project"]["getBySlug"]>;
};
export const SolutionSelectBar: React.FC<SolutionSelectBarProps> = t0 => {
  const $ = _c(60);
  let restProps;
  let selectedProject;
  if ($[0] !== t0) {
    ({
      selectedProject,
      ...restProps
    } = t0);
    $[0] = t0;
    $[1] = restProps;
    $[2] = selectedProject;
  } else {
    restProps = $[1];
    selectedProject = $[2];
  }
  const {
    LL
  } = useI18nContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    projectSlug: t1,
    solutionSlug: t2,
    setSolution
  } = usePlaygroundSlugs();
  const projectSlug = t1 === undefined ? "" : t1;
  const solutionSlug = t2 === undefined ? "" : t2;
  const solutions = selectedProject.data?.solutions;
  const trpcUtils = api.useUtils();
  let t3;
  if ($[3] !== projectSlug || $[4] !== trpcUtils.project.getBySlug) {
    t3 = solutions_0 => {
      trpcUtils.project.getBySlug.setData(projectSlug, prevData => {
        if (!prevData) {
          return prevData;
        }
        return {
          ...prevData,
          solutions: solutions_0
        };
      });
    };
    $[3] = projectSlug;
    $[4] = trpcUtils.project.getBySlug;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const updateSolutionsCache = t3;
  let t4;
  if ($[6] !== projectSlug || $[7] !== selectedProject.data?.id || $[8] !== solutionSlug || $[9] !== trpcUtils.project.getBySlug || $[10] !== trpcUtils.project.getSolutionBySlug) {
    t4 = async slug => {
      await trpcUtils.project.getBySlug.invalidate(projectSlug);
      await trpcUtils.project.getSolutionBySlug.invalidate({
        projectId: selectedProject.data?.id || "",
        slug: slug || solutionSlug
      });
    };
    $[6] = projectSlug;
    $[7] = selectedProject.data?.id;
    $[8] = solutionSlug;
    $[9] = trpcUtils.project.getBySlug;
    $[10] = trpcUtils.project.getSolutionBySlug;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const invalidateQueries = t4;
  let t5;
  if ($[12] !== invalidateQueries || $[13] !== setSolution) {
    t5 = {
      onSuccess: async data => {
        await invalidateQueries(data.slug);
        setSolution(data.slug);
      }
    };
    $[12] = invalidateQueries;
    $[13] = setSolution;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const addSolution = api.project.addSolution.useMutation(t5);
  let t6;
  if ($[15] !== updateSolutionsCache) {
    t6 = {
      onSuccess: async newList => {
        updateSolutionsCache(newList);
      }
    };
    $[15] = updateSolutionsCache;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const reorderSolutions = api.project.reorderSolutions.useMutation(t6);
  let t7;
  if ($[17] !== invalidateQueries || $[18] !== setSolution || $[19] !== solutionSlug) {
    t7 = {
      onSuccess: async data_0 => {
        await invalidateQueries();
        if (solutionSlug === data_0.slug) {
          setSolution("");
        }
      }
    };
    $[17] = invalidateQueries;
    $[18] = setSolution;
    $[19] = solutionSlug;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const deleteSolution = api.project.deleteSolution.useMutation(t7);
  const isLoading = selectedProject.isLoading || addSolution.isPending || deleteSolution.isPending;
  const isEditable = useAppSelector(selectIsEditable);
  let t8;
  if ($[21] !== setSolution) {
    t8 = solution => {
      setSolution(solution.slug);
    };
    $[21] = setSolution;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const handleSolutionClick = t8;
  let t9;
  if ($[23] !== addSolution || $[24] !== selectedProject.data || $[25] !== solutionSlug || $[26] !== solutions?.length) {
    t9 = () => {
      if (!selectedProject.data) {
        return;
      }
      const order = solutions?.length ?? 0;
      addSolution.mutate({
        projectId: selectedProject.data.id,
        order,
        referenceSolutionSlug: solutionSlug
      });
    };
    $[23] = addSolution;
    $[24] = selectedProject.data;
    $[25] = solutionSlug;
    $[26] = solutions?.length;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const handleAddSolution = t9;
  let t10;
  if ($[28] !== handleSolutionClick) {
    t10 = solution_0 => {
      handleSolutionClick(solution_0);
      setIsModalOpen(true);
    };
    $[28] = handleSolutionClick;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const handleSolutionEdit = t10;
  let t11;
  if ($[30] !== reorderSolutions || $[31] !== selectedProject.data || $[32] !== solutions || $[33] !== updateSolutionsCache) {
    t11 = result => {
      if (!result.destination || result.destination.index === result.source.index || !solutions || !selectedProject.data) {
        return;
      }
      const {
        source,
        destination
      } = result;
      const newList_0 = Array.from(solutions);
      const removed = newList_0.splice(source.index, 1);
      newList_0.splice(destination.index, 0, ...removed);
      updateSolutionsCache(newList_0);
      reorderSolutions.mutate({
        projectId: selectedProject.data.id,
        solutionIds: newList_0.map(_temp)
      });
    };
    $[30] = reorderSolutions;
    $[31] = selectedProject.data;
    $[32] = solutions;
    $[33] = updateSolutionsCache;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const onItemDragEnd = t11;
  let t12;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setIsModalOpen(false);
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== isModalOpen) {
    t13 = <SolutionModal open={isModalOpen} onClose={t12} />;
    $[36] = isModalOpen;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const t14 = !selectedProject.data;
  let t15;
  if ($[38] !== LL) {
    t15 = LL.ADD_NEW_SOLUTION();
    $[38] = LL;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== LL || $[41] !== handleSolutionClick || $[42] !== handleSolutionEdit || $[43] !== isEditable || $[44] !== isLoading || $[45] !== solutionSlug || $[46] !== solutions) {
    t16 = (_, droppableSnapshot) => solutions?.map((solution_2, index) => <DraggableSelectBarChip id={solution_2.id} key={solution_2.id} index={index} droppableSnapshot={droppableSnapshot} editLabel={LL.EDIT_SOLUTION()} isCurrent={solution_2.slug === solutionSlug} isEditable={isEditable} label={solution_2.title} disabled={isLoading} onClick={() => handleSolutionClick(solution_2)} onEditClick={() => handleSolutionEdit(solution_2)} />);
    $[40] = LL;
    $[41] = handleSolutionClick;
    $[42] = handleSolutionEdit;
    $[43] = isEditable;
    $[44] = isLoading;
    $[45] = solutionSlug;
    $[46] = solutions;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== handleAddSolution || $[49] !== isEditable || $[50] !== isLoading || $[51] !== onItemDragEnd || $[52] !== restProps || $[53] !== t14 || $[54] !== t15 || $[55] !== t16) {
    t17 = <DraggableSelectBarList droppableId="droppable-solutions" isLoading={isLoading} isEmpty={t14} onItemDragEnd={onItemDragEnd} addItemTitle={t15} handleAddItem={handleAddSolution} isEditable={isEditable} {...restProps}>{t16}</DraggableSelectBarList>;
    $[48] = handleAddSolution;
    $[49] = isEditable;
    $[50] = isLoading;
    $[51] = onItemDragEnd;
    $[52] = restProps;
    $[53] = t14;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== t13 || $[58] !== t17) {
    t18 = <>{t13}{t17}</>;
    $[57] = t13;
    $[58] = t17;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  return t18;
};
function _temp(solution_1) {
  return solution_1.id;
}